/**
 * 統一錯誤日誌記錄工具
 * 將前端錯誤統一存儲到後端的 client_logs 表（log_type = 'error'），並發送到 Sentry
 */

import { getApiBase } from '../api/apiUtils';
import { captureException } from './sentry.js';
import { getRequestId } from './requestId.js';

/**
 * 錯誤嚴重程度
 */
export type ErrorSeverity = 'error' | 'warning' | 'info';

/**
 * 結構化錯誤日誌欄位
 */
interface StructuredErrorFields {
  // 事件識別
  event?: string; // 事件名稱（如 'component_error'、'api_error'、'unhandled_rejection'）
  errorCode?: string; // 業務錯誤代碼（如 'API_ERROR'、'NETWORK_ERROR'）

  // 前端上下文
  component?: string; // 組件名稱
  userAction?: string; // 使用者操作（如 'click_submit'、'navigate_to_page'）
  url?: string; // 當前 URL
}

/**
 * 記錄錯誤到後端 client_logs 表（log_type = 'error'）
 *
 * @param error - 錯誤物件或錯誤訊息
 * @param options - 額外選項
 */
export async function logErrorToBackend(
  error: Error | string | unknown,
  options?: {
    severity?: ErrorSeverity;
    componentStack?: string;
    context?: Record<string, unknown>;
    fields?: StructuredErrorFields;
    skipConsole?: boolean;
  }
): Promise<void> {
  try {
    // 解析錯誤訊息和堆疊
    let message = 'Unknown error';
    let stackTrace: string | undefined;

    if (error instanceof Error) {
      message = error.message || error.toString();
      stackTrace = error.stack;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      message = String(error);
    }

    // 合併結構化欄位
    const fields = options?.fields || {};
    const context = options?.context || {};

    // 取得 Request ID（從後端 Response Header 取得）
    const requestId = getRequestId();

    // 檢查是否為 Lite 功能（有 groupId 且為 Lite 格式：C 開頭，33 字元，無連字號）
    const groupId = context.groupId || context.group_id || null;
    const isLiteContext =
      groupId &&
      typeof groupId === 'string' &&
      groupId.length === 33 &&
      groupId.startsWith('C') &&
      !groupId.includes('-');

    // 準備錯誤日誌資料（統一使用 client_logs 格式）
    const severity = options?.severity || 'error';

    // 對於 Lite 功能，不傳送 tenantId（Lite 功能不使用 tenant_id）
    // 只傳送 groupId 在 context 中
    const sanitizedContext = { ...context };
    if (isLiteContext) {
      // 移除 tenantId，避免誤用
      delete sanitizedContext.tenantId;
      delete sanitizedContext.tenant_id;
    }

    const errorLogEntry = {
      // 使用統一的 client_logs API 格式，但標記為錯誤日誌
      level: severity as 'error' | 'warning' | 'info', // 對應到 client_logs 的 level
      message: message.substring(0, 5000), // 限制長度
      data: {
        // 將錯誤專用欄位放在 data 中
        stackTrace: stackTrace?.substring(0, 50000) || undefined,
        componentStack:
          options?.componentStack?.substring(0, 50000) || undefined,
        // 結構化欄位
        ...(fields.event ? { event: fields.event } : {}),
        ...(fields.errorCode ? { errorCode: fields.errorCode } : {}),
        ...(fields.component ? { component: fields.component } : {}),
        ...(fields.userAction ? { userAction: fields.userAction } : {}),
        // Request ID（從後端 Response Header 取得）
        ...(requestId ? { requestId } : {}),
        // 其他上下文（已清理 tenantId）
        ...sanitizedContext,
      },
      timestamp: new Date().toISOString(),
      url: fields.url || window.location.href,
      userAgent: navigator.userAgent,
      // tenantId 和 lineUserId 會在後端從 context 中提取
      // 對於 Lite 功能，不傳送 tenantId
      tenantId: isLiteContext
        ? undefined
        : context.tenantId || context.tenant_id || undefined,
      lineUserId: context.lineId || context.lineUserId || undefined,
    };

    // 在開發環境中輸出到控制台
    if (!options?.skipConsole && import.meta.env.MODE === 'development') {
      console.error('[ErrorLogger]', {
        severity,
        message: errorLogEntry.message,
        url: errorLogEntry.url,
        hasStack: !!errorLogEntry.data.stackTrace,
        hasComponentStack: !!errorLogEntry.data.componentStack,
      });
    }

    // 發送到統一的 client_logs API（後端會根據資料格式判斷為錯誤日誌）
    // 注意：後端需要支援錯誤日誌格式，我們暫時保留 /api/logs/error 作為兼容
    // 但內部會統一使用 client_logs 表
    const apiBase = getApiBase();

    // 使用 /api/logs/error endpoint（後端已更新為使用 client_logs 表）
    const response = await fetch(`${apiBase}/api/logs/error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        severity,
        message: errorLogEntry.message,
        stack_trace: errorLogEntry.data.stackTrace,
        component_stack: errorLogEntry.data.componentStack,
        context: errorLogEntry.data,
        user_agent: errorLogEntry.userAgent,
      }),
    });

    if (!response.ok) {
      // 記錄失敗時，只在開發環境中顯示警告
      if (import.meta.env.MODE === 'development') {
        console.warn(
          '[ErrorLogger] 錯誤日誌發送失敗:',
          response.status,
          response.statusText
        );
      }
    }

    // 同時發送到 Sentry（僅錯誤級別）
    if (severity === 'error') {
      captureException(error instanceof Error ? error : new Error(message), {
        level: 'error',
        tags: {
          event: fields.event || 'frontend_error',
          errorCode: fields.errorCode || 'UNKNOWN_ERROR',
          ...(fields.component ? { component: fields.component } : {}),
        },
        extra: {
          ...errorLogEntry.data,
          hasStack: !!errorLogEntry.data.stackTrace,
          hasComponentStack: !!errorLogEntry.data.componentStack,
        },
      });
    }
  } catch (logError) {
    // 錯誤日誌記錄本身失敗時，只在開發環境中顯示警告
    // 避免錯誤日誌記錄失敗導致無限迴圈
    if (import.meta.env.MODE === 'development') {
      console.warn('[ErrorLogger] 錯誤日誌記錄失敗:', logError);
    }
  }
}

/**
 * 記錄警告
 */
export async function logWarning(
  message: string,
  context?: Record<string, unknown>
): Promise<void> {
  await logErrorToBackend(message, {
    severity: 'warning',
    context,
  });
}

/**
 * 記錄資訊
 */
export async function logInfo(
  message: string,
  context?: Record<string, unknown>
): Promise<void> {
  await logErrorToBackend(message, {
    severity: 'info',
    context,
  });
}
